import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { Button } from '../../../components/ui/Button';
import { Spinner } from '../../../components/ui/Spinner';
import { MAX_CARDS_PER_USER } from '../../../lib/constants';
import { ShareModal } from '../../../components/app/ShareModal';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

export const CardsList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [processingId, setProcessingId] = useState(null);
  
  // Share Modal State
  const [shareCard, setShareCard] = useState(null);

  const fetchCards = async () => {
    try {
      const { data, error } = await supabase
        .from('cards')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCards(data || []);
    } catch (err) {
      console.error('Error fetching cards:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) fetchCards();
  }, [user]);

  const handleStatusChange = async (card, newStatus) => {
    if (newStatus === 'archived') {
      const confirmed = window.confirm(`Are you sure you want to archive "${card.internal_name}"? It will be removed from your active cards list.`);
      if (!confirmed) return;
    } else if (newStatus === 'draft' && card.status === 'published') {
      const confirmed = window.confirm("The card will no longer be publicly accessible. Continue?");
      if (!confirmed) return;
    }

    setProcessingId(card.id);
    try {
      const { error } = await supabase
        .from('cards')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', card.id)
        .eq('user_id', user.id);

      if (error) throw error;
      await fetchCards();
    } catch (err) {
      alert(err.message || 'Failed to update card status.');
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-32"><Spinner size="lg" /></div>;
  }

  const activeCards = cards.filter(c => c.status !== 'archived');
  const archivedCards = cards.filter(c => c.status === 'archived');
  const displayCards = activeTab === 'active' ? activeCards : archivedCards;
  const hasReachedLimit = activeCards.length >= MAX_CARDS_PER_USER;

  return (
    <div className="max-w-6xl pb-16">
      {shareCard && <ShareModal card={shareCard} user={user} onClose={() => setShareCard(null)} />}

      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Your Cards</h1>
          <p className="mt-2 text-[15px] font-medium text-slate-500">Manage your digital professional identities.</p>
        </div>
        <div>
          {hasReachedLimit ? (
            <div className="text-sm text-amber-700 font-bold bg-amber-50 px-4 py-2.5 rounded-xl border border-amber-200 shadow-sm">
              Limit reached ({MAX_CARDS_PER_USER}/{MAX_CARDS_PER_USER})
            </div>
          ) : (
            <Button onClick={() => navigate('/app/cards/new')} className="rounded-xl px-5">
              <SafeIcon icon={FiIcons.FiPlus} className="mr-2 h-4 w-4" /> Create Card
            </Button>
          )}
        </div>
      </header>

      {/* Tabs - Modern segmented control style */}
      <div className="flex space-x-1 bg-slate-200/50 p-1 rounded-xl w-fit mb-8 shadow-sm">
        <button 
          onClick={() => setActiveTab('active')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${activeTab === 'active' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}
        >
          Active ({activeCards.length})
        </button>
        <button 
          onClick={() => setActiveTab('archived')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${activeTab === 'archived' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}
        >
          Archived ({archivedCards.length})
        </button>
      </div>

      {displayCards.length === 0 ? (
        <div className="bg-white rounded-[2rem] border border-slate-200/60 border-dashed p-16 text-center shadow-sm">
          <div className="w-20 h-20 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-slate-100">
            <SafeIcon icon={activeTab === 'active' ? FiIcons.FiCreditCard : FiIcons.FiArchive} className="text-3xl text-slate-400" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-3 tracking-tight">
            {activeTab === 'active' ? 'Create your first Meetoi card' : 'No archived cards'}
          </h3>
          <p className="text-[15px] font-medium text-slate-500 mb-8 max-w-sm mx-auto leading-relaxed">
            {activeTab === 'active' ? 'Build a professional digital identity you can share anywhere.' : 'Cards you archive will appear here.'}
          </p>
          {activeTab === 'active' && !hasReachedLimit && (
            <Button size="lg" onClick={() => navigate('/app/cards/new')} className="rounded-xl">Create Card</Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayCards.map(card => (
            <div key={card.id} className="bg-white rounded-3xl border border-slate-200/60 shadow-sm overflow-hidden flex flex-col transition-all duration-300 hover:shadow-md hover:border-slate-300 group">
              <div className="p-6 flex-1">
                <div className="flex justify-between items-start mb-5">
                  <div className="w-14 h-14 rounded-full overflow-hidden bg-slate-100 border border-slate-200/60">
                    {card.profile_image_url ? (
                      <img src={card.profile_image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400">
                        <SafeIcon icon={FiIcons.FiUser} className="text-xl" />
                      </div>
                    )}
                  </div>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider
                    ${card.status === 'published' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100/50' : 
                      card.status === 'archived' ? 'bg-slate-100 text-slate-600 border border-slate-200' : 
                      'bg-blue-50 text-blue-700 border border-blue-100/50'}`}>
                    {card.status}
                  </span>
                </div>
                
                <h3 className="text-xl font-bold text-slate-900 truncate mb-1 tracking-tight" title={card.internal_name}>
                  {card.internal_name}
                </h3>
                <p className="text-[15px] font-medium text-slate-500 truncate mb-4">{card.full_name} • {card.job_title}</p>
                
                {activeTab === 'active' && card.status === 'published' ? (
                  <div className="mb-5">
                    <p className="text-xs text-blue-600 font-bold truncate bg-blue-50/50 px-2.5 py-1.5 rounded-lg inline-block border border-blue-100/50">
                      meetoi.com/c/{card.public_slug}
                    </p>
                  </div>
                ) : (
                  <div className="h-8 mb-5"></div>
                )}
                
                <div className="text-xs font-medium text-slate-400 space-y-1.5">
                  <p>Template: <span className="capitalize text-slate-600">{card.template_id}</span></p>
                  <p>Updated: <span className="text-slate-600">{new Date(card.updated_at).toLocaleDateString()}</span></p>
                </div>
              </div>
              
              <div className="bg-slate-50/80 px-5 py-4 border-t border-slate-100/80 flex items-center justify-between gap-2 flex-wrap">
                {activeTab === 'active' ? (
                  <>
                    <Button variant="secondary" size="sm" className="flex-1 rounded-xl" onClick={() => navigate(`/app/cards/${card.id}/edit`)}>
                      Edit
                    </Button>
                    {card.status === 'published' && (
                      <Button variant="secondary" size="sm" className="px-3 rounded-xl" onClick={() => setShareCard(card)} title="Share Card">
                        <SafeIcon icon={FiIcons.FiShare2} />
                      </Button>
                    )}
                    <div className="w-px h-6 bg-slate-200 mx-1"></div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-slate-400 hover:text-red-600 px-3 rounded-xl hover:bg-red-50" 
                      onClick={() => handleStatusChange(card, 'archived')} 
                      isLoading={processingId === card.id} 
                      title="Archive Card"
                    >
                      <SafeIcon icon={FiIcons.FiArchive} className="h-4 w-4" />
                    </Button>
                  </>
                ) : (
                  <Button variant="secondary" size="sm" className="w-full rounded-xl" onClick={() => handleStatusChange(card, 'draft')} isLoading={processingId === card.id}>
                    Restore to Draft
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};